import { ApolloQueryResult, useQuery } from '@apollo/client';
import { get, toNumber } from 'lodash';
import defaultTo from 'lodash/defaultTo';
import React, { memo, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import Button, { ButtonGroup, LoadingButton } from '@atlaskit/button';
import { Checkbox } from '@atlaskit/checkbox';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import InfoIcon from '@atlaskit/icon/glyph/info';
import SectionMessage from '@atlaskit/section-message';
import Textfield from '@atlaskit/textfield';
import Tooltip from '@atlaskit/tooltip';
import { AnalyticsTrackAction, AnalyticsTrackSubject, analytics } from '@atlassian/temp-analytics';
import { BillingStatus, PricingPlanType } from '@atlassian/cssxp-ccp-types';
import {
  BillingFrequency,
  EntitlementDetails,
  FetchOrdersForEntitlementQuery,
  FetchOrdersForEntitlementQueryVariables,
  FetchParentEntitlementDetailsQuery,
  FetchParentEntitlementDetailsQueryVariables,
  FetchTransactionAccountForEntitlementQuery,
  FetchTransactionAccountForEntitlementQueryVariables,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  LinkedProductTypes,
  Maybe,
  PricingType,
  Promotion,
  RelationshipPathType,
  SortDirection,
  SubscriptionScheduleAction,
  TransactionAccount,
} from '@css-xp/graphtypes/src/generated-types-new';
import { SignalFxFEPerfStore } from '@atlassian/temp-helper/constants';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import { logTimeStamp } from '@atlassian/temp-metal';
import { getBillEstimate, updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { CancellationReasonCodes, OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement';
import {
  PromotionInstance,
  UpdateOrderRequest,
  EndsAtType,
  CCPPricing,
  CustomOrderEntitlement,
} from '@atlassian/cssxp-typescommon';
import {
  EntitlementEdition,
  getUserTier,
  isFreePlanEntitlement,
  notActivePricingStatus,
  constructCustomOrderRequest,
  MARKETPLACE_PROMO_CODE,
} from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_ORDER_BILL_ESTIMATION,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
  OMS_DELAY_FOR_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_OMS_SERVICE_V2,
  ENABLE_POA_POST_DELETE_REACTIVATION,
} from '@atlassian/cssxp-utils-generic';
import { isJiraProduct, isProductSkipTrialEligible, isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalType, EntityType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { ApprovalFormSection, useApprovals } from '@css-xp/apd-process-manager';
import { TaskDetailAction, useCancelRequest, useTaskList } from '@css-xp/apd-task-manager';
import {
  CancellationPendingLozenge,
  EntitlementIcon,
  ErrorComponents,
  Icon,
  InfoPanel,
  InvoiceGroupSelectItem,
  NotificationTypes,
  PromotionItemDetail,
  isDiscretionaryDiscountPromotion,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
} from '@css-xp/base';
import { TransactionAccountContextType } from '@css-xp/base/common-context';
import {
  LinkedEntitlementsTable,
  currentEntitlementType,
  getChargeElement,
  getLinkedEntitlementsDetails,
  getParentContainerId,
  getDeletedLozenge,
} from '@css-xp/base/entitlement-utils';
import { fetchParentEntitlementDetails } from '@css-xp/base/entitlement-utils/src/queries/fetch-parent-entitlement-details';
import { withApollo } from '@css-xp/base/graphql-provider';
import { getFormError } from '../../../../entitlement-page/src/components/entitlement-page-utils';
import { fetchOrdersForEntitlement } from '../../../../entitlement-page/src/queries/fetch-orders-for-entitlement';
import { fetchTransactionAccountForEntitlement } from '../../../../entitlement-page/src/queries/fetch-transaction-account-for-entitlement';
import { getCustomPromotionInstance } from '../../utils/promotion-utils';
import WarningDialog from '../../warning-dialog';
import './entitlement-cancel-active-subscription-pane.scss';
import { EntitlementReactivationPane } from './entitlement-reactivation-pane';
import { creditPolicyResponse, txaPushPaymentStatus } from '@atlassian/cssxp-types-billing';

interface Props {
  entitlementId: string;
  subscriptionStatus: boolean;
  entitlementNumber: EntitlementDetails['entitlementNumber'];
  entitlement: EntitlementDetails;
  transactionAccountContext: TransactionAccountContextType;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
}

interface OfferingSelectItem {
  label: string;
  value: {
    id: string;
  };
}

interface CustomValues {
  value: string;
  label: string;
}

export interface FormData {
  offeringSelectedItem?: OfferingSelectItem;
  supportTicketNumber?: string | null;
  handNote?: string | null;
  pricingPlan?: string;
  promotionId?: { promotion: Promotion };
  billingFrequency?: string;
  reasonCode?: string;
  customDuration?: CustomValues[];
  customIterations?: string[];
  customReason?: CustomValues[];
  customValue?: string[];
  skipTrial?: boolean;
  newPlan?: {
    label: string;
    value: string;
  };
  invoiceGroupId?: InvoiceGroupSelectItem;
  pricePlan?: {
    label: string;
    value: string;
    chargeElement: string;
    chargeType?: string;
    pricingPlanType?: string;
  };
  nextBillingAnchorTimestamp?: number;
  supportTicketLink?: string;
  comments?: string;
  offeringQuantity?: string;
  pricePlanId?: string;
}

export interface FormState {
  billingAnchorTimestamp?: number | null;
  endTimestamp?: number | null;
}

export interface TermPaymentValidation {
  response?: creditPolicyResponse;
  loading: boolean;
}

export type Estimate = {
  value?: number;
  percentage?: number;
  error?: any;
  loading?: boolean;
  response?: any;
};

export enum OrderType {
  CANCELLATION_ORDER = 'CANCELLATION_ORDER',
  REACTIVATION_ORDER = 'REACTIVATION_ORDER',
}

const viewData = {
  CANCELLATION_ORDER: {
    title: 'Cancel subscription',
    button_name: 'Cancel subscription',
    test_id: 'cancel-subscription',
    button_appearance: 'warning',
    banner_content() {
      return (
        <>
          <p>No refunds will be generated as part of this cancellation</p>
          <p>Refunds will first need to be requested from the invoice page.</p>
        </>
      );
    },
    success_message(_freeSubscription?: boolean) {
      return (
        <>
          <p>
            Unless ‘Effective immediately’ was selected, cancellation will take place at the end of the current billing
            cycle. Free plans will be canceled immediately.
          </p>
          <Link to={`#order-history`}>View order history</Link>
        </>
      );
    },
  },
  REACTIVATION_ORDER: {
    title: 'Reactivate Subscription',
    button_name: 'Reactivate',
    test_id: 'reactivate-subscription',
    button_appearance: 'default',
    banner_content() {
      return (
        <p>
          Reactivation of the subscription will be immediate. For paid plans, an invoice will be generated in 24 hours.
        </p>
      );
    },
    success_message(freeSubscription?: boolean) {
      return (
        <>
          <p>
            {!freeSubscription && <>A new invoice is being generated and will be available in 24 hours. </>}
            <>
              You can view the changes in the
              <strong> Order history</strong> tab.
            </>
          </p>
          <Link to={`#order-history`}>View order history</Link>
        </>
      );
    },
  },
};

const getSucessNotificationMessage = (viewKey: OrderType, isFreeSubscribed?: boolean) => {
  const content: React.ReactNode = viewData[viewKey].success_message(isFreeSubscribed);
  return <div>{content}</div>;
};

const getPromotionsToAdd = (transformedData: any) => {
  const promotions = Object.keys(transformedData)
    .sort()
    .filter((key) => key.trim().includes('promotionId'));

  const promotionsToAdd: any[] = [];
  promotions?.forEach((key: string, index: number) => {
    // Do not add a promotion id (instead add custom promotion id) for any discretionary/custom promotion
    let promotionToAdd;

    if (!isDiscretionaryDiscountPromotion(transformedData, key)) {
      const promotionDefinition: PromotionInstance['promotionDefinition'] = {};

      if (transformedData[key]?.promotion?.type === MARKETPLACE_PROMO_CODE) {
        promotionDefinition.promotionCode = transformedData.promoCode;
      } else {
        promotionDefinition.promotionId = transformedData[key]?.value;
      }

      promotionToAdd = {
        promotionDefinition,
      };
    } else {
      promotionToAdd = getCustomPromotionInstance(index, transformedData);
    }

    promotionToAdd && promotionsToAdd.push(promotionToAdd);
  });

  if (transformedData?.partnerPromotion?.id) {
    promotionsToAdd.push({
      promotionInstanceId: null,
      promotionDefinition: {
        promotionId: transformedData?.partnerPromotion?.id,
        promotionCode: null,
        customisedValues: null,
      },
    });
  }
  const filteredPromotionsToAdd = promotionsToAdd.filter(
    (promotionToAdd) =>
      !(
        !promotionToAdd.promotionDefinition.customisedValues &&
        !promotionToAdd.promotionDefinition.promotionId &&
        !promotionToAdd.promotionDefinition.promotionCode
      )
  );
  return filteredPromotionsToAdd;
};

export const newAddedPromotionValueResolver = (
  customPromotionId: string,
  formState: any,
  index: number,
  customPromotionDetails?: PromotionItemDetail
) => {
  return {
    promotionDefinition: {
      promotionId: customPromotionId,
      customisedValues: !customPromotionId
        ? null
        : {
            applicationReason: {
              id: defaultTo(customPromotionDetails?.reason ?? formState.customReason?.[index]?.value, ''),
            },
            benefits: [
              {
                duration: defaultTo(customPromotionDetails?.duration ?? formState.customDuration?.[index]?.value, ''),
                iterations: toNumber(
                  defaultTo(customPromotionDetails?.iterations ?? formState.customIterations?.[index], undefined)
                ),
                value: toNumber(
                  defaultTo(customPromotionDetails?.discountPercentage ?? formState.customValue?.[index], undefined)
                ),
              },
            ],
          },
    },
    promotionInstanceId: null,
  };
};

export const CancelActiveSubscription = ({
  entitlementId,
  subscriptionStatus,
  entitlementNumber,
  entitlement,
  transactionAccountContext,
  refetch,
}: Props) => {
  const startTime = new Date().getTime();
  const [isEnabled] = useFeatureToggle();
  const { closeSidebar } = useSidebar();
  const [reasonCode, setReasonCode] = useState<string>('Select');
  const [showField, setShowField] = useState<boolean>(true);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState<boolean>(false);
  const [isReactivationDisabled, setIsReactivationDisabled] = useState<boolean>(false);
  const [termPaymentValidation, setTermPaymentValidation] = useState<TermPaymentValidation>({
    loading: false,
    response: { status: txaPushPaymentStatus.INELIGIBLE },
  });
  const [selectedReasonCode, setSelectedReasonCode] = useState<string | undefined>(undefined);
  const { getTransactionAccount, data: transactionAccountData, getCreditDetails } = transactionAccountContext ?? {};
  const [isFieldDisable, setIsFieldDisable] = useState<boolean>(false);
  const [hasCustomPromotions, setCustomPromotions] = useState<boolean>(false);
  const isEntRelationFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isOMSDelayFeatureEnable = isEnabled(OMS_DELAY_FOR_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);
  const isPOAEnabled = isEnabled(ENABLE_POA_POST_DELETE_REACTIVATION);
  const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = useState(false);
  const [estimatedBill, setEstimatedBill] = React.useState<Partial<Estimate>>({
    value: 0,
    percentage: 0,
    loading: false,
  });
  const formValueRef = React.useRef<any>();
  const [formState, updateFormState] = React.useState<FormState>({});
  const creditDetails = getCreditDetails && getCreditDetails(transactionAccountData);
  const transactionAccount = getTransactionAccount(transactionAccountData) as TransactionAccount;

  const [isBillingProfileInValid, setIsBillingProfileInValid] = React.useState<boolean>(false);
  const [grandTotal, setGrandTotal] = React.useState<number>(0);
  const linkedIG = useMemo(() => {
    return ((transactionAccount?.availableInvoiceGroups?.nodes ?? []) as InvoiceGroup[])?.find(
      (invoiceGroup: InvoiceGroup) => {
        return invoiceGroup.id === entitlement.billingGroupId;
      }
    );
  }, [entitlement, transactionAccount]);
  const [currentInvoiceGroup, setCurrentInvoiceGroup] = React.useState(linkedIG);

  const linkedEntsTableVisible = useMemo(() => {
    return (
      isEntRelationFeatureEnable &&
      (!!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length)
    );
  }, [entitlement, isEntRelationFeatureEnable]);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  const isImmediateCancellation = () => {
    if (isJiraProduct(entitlement.productId)) {
      return !isOMSDelayFeatureEnable;
    }
    if (entitlement?.isSandbox) {
      return true;
    }
    return false;
  };

  const approvalEval = React.useRef<any>();

  const [isImmediateFlag, setIsImmediateFlag] = useState<boolean>(isImmediateCancellation());

  const [linkedEntitlementsData, setLinkedEntitlementsData] = useState<Array<LinkedDestinationEntitlement>>([]);

  const familyEntitlement = linkedEntitlementsData?.find(
    (linkedEntitlementData) => linkedEntitlementData.transactionAccountId
  );

  const [cancelledProductCount, setCancelledProductCount] = useState<number>(1);

  const productName = entitlement?.productName || 'Unnamed product';
  const productId = entitlement.productId;
  const isCloud = entitlement.platform?.trim().toUpperCase() === 'CLOUD';
  const isAddOn = entitlement.parentSen ? true : false;

  const id = entitlement.id;
  const isReactivationFlow = !subscriptionStatus;

  const isSubscriptionDeleted = getDeletedLozenge(entitlement.billingStatus, entitlement.provisioningStatus);

  const { data: parentEntitlementDetailResponse } = useQuery<
    FetchParentEntitlementDetailsQuery,
    FetchParentEntitlementDetailsQueryVariables
  >(fetchParentEntitlementDetails, {
    variables: {
      id,
    },
    skip: !isReactivationFlow,
    displayName: 'fetchLinkedParentEntitlements',
    notifyOnNetworkStatusChange: true,
  });

  const parentEntitlementDetails = useMemo(() => {
    return parentEntitlementDetailResponse?.entitlementDetails?.parentEntitlementDetails;
  }, [parentEntitlementDetailResponse]);

  const familyEntitlmentTransactionAccountResult = useQuery<
    FetchTransactionAccountForEntitlementQuery,
    FetchTransactionAccountForEntitlementQueryVariables
  >(fetchTransactionAccountForEntitlement, {
    variables: { entitlementId: familyEntitlement?.entitlementId },
    skip: !familyEntitlement?.entitlementId,
  });
  const familyEntitlementTransactionAccount = useMemo(() => {
    return familyEntitlmentTransactionAccountResult?.data?.entitlementDetails?.transactionAccount;
  }, [familyEntitlmentTransactionAccountResult]);

  const { data, error, loading } = useQuery<FetchOrdersForEntitlementQuery, FetchOrdersForEntitlementQueryVariables>(
    fetchOrdersForEntitlement,
    {
      variables: {
        id,
        searchOptions: {
          first: 1,
          sort: { sortFields: ['createdDate', 'id'], sortFieldsDirections: [SortDirection.DESC, SortDirection.DESC] },
        },
      },
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      skip: !id || !isReactivationFlow,
    }
  );

  const isBillEsimationEnabled = isEnabled(ENABLE_ORDER_BILL_ESTIMATION);
  const { isDiscountApprovalFlowEnabled, isPricingPlanChangeApprovalFlowEnabled } = useApprovalDeskPermissions();

  const {
    cancelApprovalRequestError,
    cancelApprovalRequestProcessing,
    submitRequestAfterApprovalCancelation,
  } = useCancelRequest({
    businessKey: entitlement.entitlementNumber,
  });

  const { hasOpenApprovalRequest, error: taskListError, data: taskListData } = useTaskList({
    businessKey: entitlementNumber,
    skip: !entitlementNumber || !isDiscountApprovalFlowEnabled || !isPricingPlanChangeApprovalFlowEnabled,
  });

  const prevReasonCode = data?.entitlementDetails?.orders?.nodes[0]?.orderLineItems?.nodes[0]?.reasonCode ?? '';

  const familyEntitlementReactivationBanner = () => {
    if (!isReactivationFlow || !familyEntitlementTransactionAccount || !transactionAccount) {
      return null;
    }
    const billingProfileflag = entitlement.activeSubscriptionBillingGroupId
      ? familyEntitlement?.billingGroupId !== entitlement.activeSubscriptionBillingGroupId
      : false;
    const oldBillingProfile = transactionAccount.availableInvoiceGroups?.nodes.find(
      (invoiceGroup) => invoiceGroup?.id === entitlement.activeSubscriptionBillingGroupId
    );
    const newBillingProfile = familyEntitlementTransactionAccount.availableInvoiceGroups?.nodes.find(
      (invoiceGroup) => invoiceGroup?.id === familyEntitlement?.billingGroupId
    );

    if (
      familyEntitlementTransactionAccount?.transactionAccountNumber === transactionAccount?.transactionAccountNumber
    ) {
      return (
        <>
          {billingProfileflag && (
            <FormSection>
              <SectionMessage appearance="warning">
                <>
                  <p>
                    To keep all family products in sync, these subscription details will also change when you
                    reactivate:
                  </p>
                  {billingProfileflag && (
                    <p>{`Billing profile: ${oldBillingProfile?.name || 'Not provided'} → ${
                      newBillingProfile?.name || 'Not provided'
                    }`}</p>
                  )}
                </>
              </SectionMessage>
            </FormSection>
          )}
        </>
      );
    }

    return (
      <>
        <FormSection>
          <SectionMessage appearance="warning">
            <>
              <p>
                To keep all family products in sync, these subscription details will also change when you reactivate:
              </p>
              <p>{`Billing account: ${transactionAccount.transactionAccountNumber} → ${familyEntitlementTransactionAccount.transactionAccountNumber}`}</p>
              {billingProfileflag && (
                <p>{`Billing profile: ${oldBillingProfile?.name || 'Not provided'} → ${
                  newBillingProfile?.name || 'Not provided'
                }`}</p>
              )}
            </>
          </SectionMessage>
        </FormSection>
      </>
    );
  };

  const headerContent = `${isAddOn ? 'AddOn/' : ''}${productName} ${entitlementNumber}`;
  const header = () => {
    return (
      <div className="header-box">
        <EntitlementIcon
          logoId={entitlement?.logoId as string}
          productId={productId as string}
          className="avatar medium"
          isCloud={isCloud}
        />
        {headerContent}
        {entitlement.scheduledChanges?.subscriptionScheduleAction === SubscriptionScheduleAction.CANCEL && (
          <CancellationPendingLozenge />
        )}
      </div>
    );
  };

  const productSkipTrialEligible = isProductSkipTrialEligible(
    defaultTo(entitlement?.offering?.productKey, ''),
    entitlement?.isApp ?? false
  );

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
    onComplete: (response) => {
      closeSidebar();
      refetch();
      analytics.sendTrackEvent({
        attributes: {
          reasonCode: selectedReasonCode,
          cancellationType: 'Active',
          supportTicketNumber: response?.metadata?.supportTicketNumber
            ? response.metadata.supportTicketNumber
            : undefined,
        },
        action: subscriptionStatus ? AnalyticsTrackAction.CANCEL : AnalyticsTrackAction.REACTIVATE,
        actionSubject: subscriptionStatus
          ? AnalyticsTrackSubject.CANCEL_SUBSCRIPTION
          : AnalyticsTrackSubject.REACTIVATE_SUBSCRIPTION,
      });
    },
  });

  const viewKey = subscriptionStatus ? OrderType.CANCELLATION_ORDER : OrderType.REACTIVATION_ORDER;
  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  const hasFreeSubscription = entitlement?.offering?.pricingType === PricingType.FREE;
  // Allow reactivation of Apps only if app is installed and there exist parents for the app.

  const onApprovalComplete = React.useCallback(() => {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('approval-submit-success')
      .withHeader('Approval request submitted')
      .withMessage('You can view your request in the Approval requests section.')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
    closeSidebar();
    refetch?.();
  }, [closeSidebar, refetch, sendSuccessNotification, setNotificationSent]);

  const {
    approvalRequired: isApprovalRequiredForDiscount,
    approvalError: approvalErrorForDiscount,
    approvalList: approvalListForDiscount,
    approvalProcessing: approvalProcessingForDiscount,
    evaluate: evaluateApprovalForDiscount,
    setApprovalRequired: setApprovalRequiredDiscount,
    approvalSlackChannel: approvalSlackChannelForDiscount,
  } = useApprovals({
    approvalType: ApprovalType.ORDER_DISCOUNT,
    onApprovalComplete,
  });

  const {
    approvalRequired: isApprovalRequiredForPricing,
    approvalError: approvalErrorForPricing,
    approvalList: approvalListForPricing,
    approvalProcessing: approvalProcessingForPricing,
    evaluate: evaluateApprovalForPricing,
    setApprovalRequired: setApprovalRequiredPricing,
    submitApproval,
    approvalSlackChannel: approvalSlackChannelForPricing,
  } = useApprovals({
    approvalType: ApprovalType.PRICING_PLAN_CHANGE,
    onApprovalComplete,
  });

  const isAppReactivationNotAllowed = useMemo(() => {
    if (isReactivationFlow && entitlement?.isApp && !loadingLinkedEntitlement) {
      if (linkedEntitlementsData?.length === 0) {
        return true;
      }
      const activeLinkedEntitlements = linkedEntitlementsData?.filter(
        (entitlementObj) => entitlementObj?.billingStatus === BillingStatus.ACTIVE
      );
      if (activeLinkedEntitlements?.length === 0) {
        return true;
      }
    }
    return false;
  }, [isReactivationFlow, linkedEntitlementsData, entitlement, loadingLinkedEntitlement]);

  const getNotificationOption = (id: string, header: string) => {
    const notificationOptions = successNotificationBuilder();
    return notificationOptions
      ?.withId(id)
      .withHeader(header)
      .withType('success')
      .withMessage(getSucessNotificationMessage(viewKey, hasFreeSubscription))
      .withDismissAfter(3000)
      .isVersion(NotificationTypes.FLAG)
      .build();
  };

  if (complete && !notificationSent) {
    let notificationOptions;
    if (subscriptionStatus) {
      notificationOptions = getNotificationOption('cancel-subscription-success', 'Task complete');
    } else {
      notificationOptions = getNotificationOption('reactivate-subscription-success', 'Subscription changes submitted');
    }

    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const getReasonCodeItem = () => {
    const reasonCodes = Object.keys(CancellationReasonCodes);
    return reasonCodes.map((code: string) => {
      return (
        <DropdownItem
          key={code}
          onClick={() => {
            //@ts-ignore
            setReasonCode(CancellationReasonCodes[code]);
            setSelectedReasonCode(code);
          }}
        >
          {CancellationReasonCodes[code as keyof typeof CancellationReasonCodes]}
        </DropdownItem>
      );
    });
  };

  const constructRequestData = (entitlementId: string, formData: FormData) => {
    const addPromotions = getPromotionsToAdd(formData);

    const promotionIdsToAdd = addPromotions
      .map((promotion) => {
        if (promotion.promotionDefinition.promotionId) {
          return promotion.promotionDefinition.promotionId;
        } else if (promotion.promotionDefinition.promotionCode) {
          const promotions = Object.keys(formData);
          const promoCodekey = promotions?.find(
            //@ts-ignore
            (key: string) => formData[key]?.promotion?.type === MARKETPLACE_PROMO_CODE
          );
          //@ts-ignore
          return formData[promoCodekey]?.value;
        }

        return undefined;
      })
      .filter((promo) => !!promo);

    const promotionsToRemove: any[] = [];
    entitlement?.promotions?.map((promotion: Maybe<Promotion>) => {
      const promotionId = promotion?.id;
      if (promotionId && !promotionIdsToAdd.includes(promotionId)) {
        promotionsToRemove.push({
          promotionDefinition: {
            promotionId: promotionId,
          },
        });
      }
    });

    let requestData: UpdateOrderRequest = {
      metadata: constructMetadata(formData),
      billingFrequency: formData.billingFrequency ?? (entitlement?.billingFrequency as string),
      entitlementId: entitlementId,
      orderType: subscriptionStatus ? OrderType.CANCELLATION_ORDER : OrderType.REACTIVATION_ORDER,
      isImmediate: subscriptionStatus ? (hasFreeSubscription ? true : isImmediateFlag ? true : false) : true,
      productId: entitlement.productId ? entitlement.productId : '',
      units: isOmsServiceV2Enabled
        ? (entitlement.unitCount as string)
        : isStatuspage(entitlement?.productId)
        ? (entitlement.unitCount as string)
        : getUserTier(entitlement?.subscription)?.toString(),
      reasonCode: formData.reasonCode || '', //@ts-ignore
      chargeQuantities: entitlement?.scheduledChanges?.chargeQuantities || [],
      chargeElement: getChargeElement(entitlement?.productId as string),
      cloudSiteId: entitlement.cloudSiteId ?? '',
      isDeleted: isSubscriptionDeleted,
      isPOAEnabled: isPOAEnabled,
    };

    if (isEntRelationFeatureEnable && !isReactivationFlow && !isOMSDelayFeatureEnable) {
      requestData.linkedEntitlements = linkedEntitlementsData?.length
        ? getLinkedEntitlementsDetails(
            entitlement,
            linkedEntitlementsData?.filter(
              (linkedEntitlementObj) => linkedEntitlementObj.billingStatus !== BillingStatus.INACTIVE
            )
          )
        : [];
    }

    if (isReactivationFlow) {
      requestData.offeringId = formData.offeringSelectedItem?.value.id ?? '';
      // Pricing plan type should have fallback value as commercial
      requestData.pricingPlanType = entitlement?.pricingPlan?.type ?? PricingPlanType.COMMERCIAL;
      requestData.addPromotions = addPromotions;
      requestData.removePromotions = promotionsToRemove;
      requestData.isPromotionStackingAllowed = addPromotions.length > 1 || promotionsToRemove.length > 0;
      if (productSkipTrialEligible) {
        requestData = {
          ...requestData,
          skipTrial: !!formData?.skipTrial,
        };
      }
      if (formData?.pricePlan) {
        requestData.pricingPlanId = formData?.pricePlan?.value;
        requestData.chargeElement = formData?.pricePlan?.chargeElement;
        requestData.chargeType = formData?.pricePlan?.chargeType;
      }

      //To check the pricing plan status while reactivating an order
      requestData.isExpiredPricingPlan = notActivePricingStatus.includes(entitlement?.pricingPlan?.status as string);

      // send endTimestamp if available
      if (formState?.endTimestamp) {
        requestData.endsAt = {
          type: EndsAtType.TIMESTAMP,
          value: formState.endTimestamp,
        };
      }
      if (formState?.billingAnchorTimestamp) {
        requestData.nextBillingAnchorTimestamp = `${formState.billingAnchorTimestamp}`;
      }

      // Add container id, if it is last parent
      if (!linkedEntitlementsData?.length) {
        const familyContainerDependence = entitlement?.relatesFromEntitlements?.find(
          (relationShipObj) => relationShipObj?.relationshipType === RelationshipPathType.FAMILY_CONTAINER
        );
        if (familyContainerDependence && parentEntitlementDetails) {
          // send both entitlement and container for reactivation
          requestData.linkedEntitlements = [
            {
              entitlementId: parentEntitlementDetails?.entitlementId,
              productId: parentEntitlementDetails?.productId as string,
              offeringId: parentEntitlementDetails?.offeringId as string,
            },
            {
              entitlementId: entitlement?.id,
              productId: entitlement?.productId as string,
              offeringId: entitlement?.offeringId as string,
              relatesFromEntitlements: [
                {
                  relationshipType: RelationshipPathType.FAMILY_CONTAINER,
                  entitlementId: parentEntitlementDetails?.entitlementId,
                  referenceType: 'ENTITLEMENT',
                },
              ],
            },
          ];
        }
      } else {
        const activeEntitlementWithTXA = linkedEntitlementsData?.filter(
          (linkedEntitlementObj) => linkedEntitlementObj?.transactionAccountId
        );

        const activeEntitlementWithBillingFrequency = linkedEntitlementsData?.find(
          (linkedEntitlementObj) => linkedEntitlementObj?.billingFrequency
        );

        if (activeEntitlementWithTXA?.length) {
          const activeEntitlementWithIGAndTXA = activeEntitlementWithTXA?.find(
            (linkedEntitlementObj) => linkedEntitlementObj?.billingGroupId
          );

          if (
            !isFreePlanEntitlement(entitlement) &&
            activeEntitlementWithIGAndTXA?.billingGroupId &&
            entitlement.billingGroupId !== activeEntitlementWithIGAndTXA.billingGroupId
          ) {
            requestData.transferInvoiceGroupId = activeEntitlementWithIGAndTXA.billingGroupId;
          }

          const activeTransactionAccountId = activeEntitlementWithTXA?.[0]?.transactionAccountId;
          if (activeTransactionAccountId && activeTransactionAccountId !== entitlement.transactionAccountId) {
            requestData.transferTransactionAccountId = activeTransactionAccountId;
          }

          if (activeEntitlementWithBillingFrequency?.billingFrequency === BillingFrequency.ANNUAL) {
            requestData.billingFrequency = BillingFrequency.ANNUAL;

            // When switching from Monthly to Annual
            // eslint-disable-next-line max-depth
            if (entitlement?.billingFrequency !== formData?.billingFrequency) {
              requestData.isBillingCycleChanged = true;
            }

            // Update user-tier value and update maximum user tier of it's parent, when reactivating app
            // eslint-disable-next-line max-depth
            if (formData?.offeringQuantity) {
              requestData.units = formData?.offeringQuantity;
            }
          }
        }
      }
      // Add relatesFrom for reactivation, if present (CCP requires this data for reactivation)
      if (entitlement?.relatesFromEntitlements?.length) {
        requestData.relatesFromEntitlements = entitlement.relatesFromEntitlements.map((entitlementRelationObj) => ({
          relationshipType: entitlementRelationObj?.relationshipType as RelationshipPathType,
          entitlementId: entitlementRelationObj?.entitlementId as string,
          referenceType: 'ENTITLEMENT',
        }));
      }
    }

    if (formData?.nextBillingAnchorTimestamp) {
      requestData.nextBillingAnchorTimestamp = `${formData?.nextBillingAnchorTimestamp}`;
    }

    if (isOmsServiceV2Enabled) {
      let pricingPlanId;
      let invoiceGroupId;
      if (isReactivationFlow) {
        pricingPlanId = formData?.pricePlan ? formData.pricePlan?.value : formData?.pricePlanId;
        invoiceGroupId = defaultTo(formData?.invoiceGroupId?.value, undefined);
      }
      const entitlementsList: CustomOrderEntitlement[] = [
        {
          entitlementId: entitlementId,
          offeringId: requestData?.offeringId,
          productId: requestData?.productId as string,
          relatesFromEntitlements: requestData?.relatesFromEntitlements,
          chargeQuantity: defaultTo(toNumber(requestData?.units), 1),
          pricingPlanId,
          pricingPlanType: requestData?.pricingPlanType as CCPPricing,
          chargeElement: requestData?.chargeElement,
          skipTrial: requestData?.skipTrial,
          transferInvoiceGroupId: requestData?.transferInvoiceGroupId,
          nextBillingAnchorTimestamp: defaultTo(toNumber(requestData?.nextBillingAnchorTimestamp), undefined),
          transferTransactionAccountId: requestData?.transferTransactionAccountId,
          isExpiredPricingPlan: requestData?.isExpiredPricingPlan,
          addPromotions: requestData?.addPromotions,
          removePromotions: requestData?.removePromotions,
          operations: requestData?.operations,
          chargeType: requestData?.chargeType,
          isImmediate: requestData?.isImmediate,
          endsAt: requestData?.endsAt,
        },
      ];

      // sending container for reactivation if it is last parent
      requestData?.linkedEntitlements?.forEach((linkedEntitlement) => {
        if (entitlement.id !== linkedEntitlement.entitlementId) {
          entitlementsList.push({
            ...linkedEntitlement,
            isImmediate: requestData?.isImmediate,
            nextBillingAnchorTimestamp: defaultTo(toNumber(requestData?.nextBillingAnchorTimestamp), undefined),
            endsAt: requestData?.endsAt,
          });
        }
      });

      const constructV2Request = {
        metadata: constructMetadata(formData),
        orderType: subscriptionStatus ? OrderItemType.CANCELLATION_ORDER : OrderItemType.REACTIVATION_ORDER,
        billingFrequency: formData.billingFrequency ?? (entitlement?.billingFrequency as string),
        reasonCode: formData?.reasonCode ? formData.reasonCode : undefined,
        entitlements: entitlementsList,
        transactionAccountId: entitlement?.transactionAccountId as string,
        invoiceGroupId,
      };
      return constructCustomOrderRequest(constructV2Request);
    }
    return requestData;
  };
  const constructMetadata = (formData: FormData) => {
    const metadata: Record<string, string> = {};
    if (formData.handNote) {
      metadata[OMSMetadata.HAND_NOTE] = formData.handNote;
    }
    if (formData.supportTicketNumber) {
      metadata[OMSMetadata.SUPPORT_TICKET_NUMBER] = formData.supportTicketNumber;
    }
    return metadata;
  };

  const actualApprovalType = React.useMemo(() => {
    return isApprovalRequiredForPricing ? ApprovalType.PRICING_PLAN_CHANGE : ApprovalType.ORDER_DISCOUNT;
  }, [isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const approvalRequiredForTypes = React.useMemo(() => {
    return isApprovalRequiredForPricing && isApprovalRequiredForDiscount
      ? [ApprovalType.ORDER_DISCOUNT, ApprovalType.PRICING_PLAN_CHANGE]
      : [];
  }, [isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const approvalList = React.useMemo(() => {
    if (isApprovalRequiredForDiscount && isApprovalRequiredForPricing) {
      return [
        ...approvalListForDiscount.filter((item) => item.approvalRequired),
        ...approvalListForPricing.filter((item) => item.approvalRequired),
      ];
    }
    if (isApprovalRequiredForDiscount) {
      return approvalListForDiscount.filter((item) => item.approvalRequired);
    }
    if (isApprovalRequiredForPricing) {
      return approvalListForPricing.filter((item) => item.approvalRequired);
    }
    return [];
  }, [approvalListForDiscount, approvalListForPricing, isApprovalRequiredForDiscount, isApprovalRequiredForPricing]);

  const buildApprovalEntityInformation = (discountPercentage: number, discountValue: number) => {
    return {
      entityType: EntityType.ENTITLEMENT,
      entityId: entitlement.id,
      entityNumber: entitlement.entitlementNumber,
      discountPercentage,
      discountValue,
    };
  };

  const isApprovalEntityEvaluatedForDiscount = async (discountPercentage = 0, discountValue = 0) => {
    const discountApproval = await evaluateApprovalForDiscount([
      {
        ...buildApprovalEntityInformation(discountPercentage, discountValue),
      },
    ]);
    approvalEval.current = { discountApproval };
    if (
      (discountApproval && !discountApproval.error && discountApproval.isApprovalRequired) ||
      !discountApproval ||
      !!discountApproval?.error
    ) {
      return true;
    }
    return false;
  };

  const isApprovalEntityEvaluateForPricing = async (params: any) => {
    const { discountValue, percentageValue, toPricingPlanType } = params;
    const pricePlanChangeApproval = await evaluateApprovalForPricing([
      {
        ...buildApprovalEntityInformation(percentageValue, discountValue),
        fromPricingPlanKey: defaultTo(entitlement?.pricingPlan?.pricingPlanType, ''),
        toPricingPlanKey: defaultTo(toPricingPlanType, ''),
      },
    ]);
    approvalEval.current = { pricePlanChangeApproval };
    if (
      (pricePlanChangeApproval && !pricePlanChangeApproval.error && pricePlanChangeApproval.isApprovalRequired) ||
      !pricePlanChangeApproval ||
      !!pricePlanChangeApproval?.error
    ) {
      return true;
    }
    return false;
  };

  const isApprovalEvaluatedForReactivation = async (formData: FormData) => {
    let discountValue,
      percentageValue = 0;
    let isDiscountEvaluated,
      isPricePlanChangeEvaluated = false;
    const promotionIds = [],
      customPromotions: PromotionInstance[] = [];
    const promotionKeys = Object.keys(formData).filter((key) => key.trim().includes('promotionId'));
    for (let i = 0; i < promotionKeys.length; i++) {
      const promotionId = get(formData, `${promotionKeys[i]}.value`, '');
      if (isDiscretionaryDiscountPromotion(formData, promotionKeys[i])) {
        const customkey = `customPromotionId-${i}`;
        // @ts-ignore
        const customPromotionId = formData[customkey]?.value;
        customPromotions.push(newAddedPromotionValueResolver(customPromotionId, formData, i));
      } else {
        if (promotionId) promotionIds.push({ promotionDefinition: { promotionId } });
      }
    }
    setCustomPromotions(!!customPromotions.length);
    if (!isBillEsimationEnabled && customPromotions.length) return;

    if (isDiscountApprovalFlowEnabled && customPromotions.length) {
      discountValue = estimatedBill?.value ?? 0;
      percentageValue = estimatedBill?.percentage ?? 0;
      isDiscountEvaluated = await isApprovalEntityEvaluatedForDiscount(percentageValue, discountValue);
    }

    if (isPricingPlanChangeApprovalFlowEnabled && formData?.pricePlan?.pricingPlanType) {
      isPricePlanChangeEvaluated = await isApprovalEntityEvaluateForPricing({
        discountValue,
        percentageValue,
        toPricingPlanType: defaultTo(formData?.pricePlan?.pricingPlanType, 0),
      });
    }
    return !isDiscountEvaluated && !isPricePlanChangeEvaluated;
  };

  const handleSumitRequest = async (formData: FormData) => {
    setIsBillingProfileInValid(false);
    if (
      !isFreePlanEntitlement(entitlement) &&
      !!formData.skipTrial &&
      isReactivationFlow &&
      (grandTotal ?? 0) > 0 &&
      !defaultTo(formData?.invoiceGroupId?.invoiceGroup?.defaultPaymentMethod, null)
    ) {
      setIsBillingProfileInValid(true);
      //@ts-ignore
      setCurrentInvoiceGroup(formData.invoiceGroupId?.invoiceGroup);
      return;
    }
    const requestData = constructRequestData(entitlementId, formData);
    if (!isDiscountApprovalFlowEnabled && !isPricingPlanChangeApprovalFlowEnabled) {
      submitForm(requestData);
      return;
    }

    if (isReactivationFlow) {
      if (isApprovalRequiredForDiscount || isApprovalRequiredForPricing) {
        submitRequestAfterApprovalCancelation(
          () =>
            submitApproval({
              approvalType: actualApprovalType,
              businessKey: entitlement.entitlementNumber as string,
              entityInformation: [
                {
                  ...buildApprovalEntityInformation(
                    defaultTo(toNumber(estimatedBill.percentage), 0),
                    defaultTo(toNumber(estimatedBill.value), 0)
                  ),
                  ...(actualApprovalType === ApprovalType.PRICING_PLAN_CHANGE
                    ? {
                        fromPricingPlanKey: defaultTo(entitlement?.pricingPlan?.pricingPlanType, ''),
                        toPricingPlanKey: formData?.pricePlan?.pricingPlanType,
                      }
                    : {}),
                },
              ],
              gsacId: formData?.supportTicketNumber,
              initiatorComments: formData?.comments,
              additionalInformation: requestData,
            }),
          hasOpenApprovalRequest
        );
        return;
      }

      const isEvaluated = await isApprovalEvaluatedForReactivation(formData);
      if (isEvaluated) {
        submitRequestAfterApprovalCancelation(() => submitForm(requestData), hasOpenApprovalRequest);
      }
    } else {
      submitRequestAfterApprovalCancelation(() => submitForm(requestData), hasOpenApprovalRequest);
    }
  };

  const handleFormSubmit = (formData: FormData) => {
    logTimeStamp(startTime, 'Cancel Subscription', SignalFxFEPerfStore.FE_USER_TASK_CANCEL_SUB_TIME);
    if (warningModalDailogVisible && subscriptionStatus) {
      setConfirmDialogOpen(true);
    } else {
      handleSumitRequest(formData);
    }
  };

  const handleConfirmDialog = (formData: FormData) => {
    setConfirmDialogOpen(false);
    handleSumitRequest(formData);
  };

  const setImmediateFlag = () => {
    if (isImmediateCancellation()) setIsImmediateFlag(true);
    else setIsImmediateFlag((current) => !current);
  };

  const isActionDisabled = useMemo(() => {
    if (loadingLinkedEntitlement || isAppReactivationNotAllowed || !!taskListError || termPaymentValidation.loading) {
      return true;
    }
    if (
      subscriptionStatus &&
      !isImmediateFlag &&
      entitlement.scheduledChanges?.subscriptionScheduleAction === SubscriptionScheduleAction.CANCEL
        ? true
        : isFieldDisable
        ? true
        : false
    ) {
      return true;
    }
    return false;
  }, [
    loadingLinkedEntitlement,
    isAppReactivationNotAllowed,
    taskListError,
    entitlement,
    isFieldDisable,
    isImmediateFlag,
    subscriptionStatus,
  ]);

  const lastParentShouldCheckOrNot = useMemo(() => {
    if (entitlement.productType === LinkedProductTypes.JIRA) {
      return true;
    } else if (entitlement.productType === LinkedProductTypes.CONFLUENCE) {
      return entitlement.edition?.toUpperCase() === EntitlementEdition.PREMIUM;
    } else return false;
  }, [entitlement]);

  if (loading) return <LoadingIndicator />;
  if (error) return <ErrorComponents.GenericErrorPage error={error} />;

  return (
    <div>
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, getState, setFieldValue }) => {
          formValueRef.current = getState;
          return (
            <form {...formProps}>
              <FormHeader title={viewData[viewKey].title} description={header()} />
              {linkedEntsTableVisible && subscriptionStatus && !entitlement.isApp && (
                <>
                  <LinkedEntitlementsTable
                    entitlementId={entitlement?.id}
                    parentContainerID={getParentContainerId(entitlement)}
                    currentEntitlementType={currentEntitlementType(entitlement)}
                    destinationEntitlementType={
                      entitlement?.isSandbox
                        ? [LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]
                        : [LinkedEntitlementTypes.PROD_ENTITLEMENT_APP]
                    }
                    findProducts={false}
                    findApps={true}
                    productType={entitlement.productType}
                    checkForLastParentExistence={lastParentShouldCheckOrNot}
                    message={'Performing this action means these subscriptions will also be canceled:'}
                    updateLinkedEntitlements={setLinkedEntitlementsData}
                    setLoading={setLoadingLinkedEntitlements}
                    linkedEntsCount={setCancelledProductCount}
                  />
                </>
              )}
              <FormSection>
                {isAppReactivationNotAllowed && (
                  <InfoPanel
                    type="error"
                    message={<div>App cannot be reactivated, for inactive parent entitlement</div>}
                  />
                )}
              </FormSection>
              <FormSection>
                {isBillingProfileInValid && (
                  <SectionMessage appearance="error" title="Update billing profile" testId="invalid-billing-profile">
                    <div>
                      <p>Subscription changes can’t be made because there’s:</p>
                      <br />
                      <ul>
                        {!currentInvoiceGroup?.defaultPaymentMethod ? (
                          <li>No payment method available for the billing profile</li>
                        ) : null}
                      </ul>
                      <br />
                      <p>
                        Add the information or switch the billing profile, then try again. Still having trouble? Contact
                        the <strong>#help-advocate-central</strong> channel.
                      </p>
                    </div>
                  </SectionMessage>
                )}
              </FormSection>
              <FormSection>
                {!!taskListError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to cancel subscription for the following reason"
                        message={taskListError.message}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {!!cancelApprovalRequestError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to cancel subscription for the following reason"
                        message={cancelApprovalRequestError.message}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {!!approvalErrorForDiscount && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to process the discount promotion for the following reason"
                        message={approvalErrorForDiscount.message}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {!!approvalErrorForPricing && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to process the pricing plan change for the following reason"
                        message={approvalErrorForPricing.message}
                      />
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                {hasOpenApprovalRequest && (
                  <InfoPanel
                    type="warning"
                    message={
                      <div className="approval-request-inflight-banner">
                        One or more approval requests are currently in progress. If you perform this action, those
                        approval requests will be canceled automatically.
                        {taskListData?.taskList?.tasks?.length && (
                          <TaskDetailAction
                            task={taskListData.taskList.tasks[0]}
                            text="View approval request details"
                            refetch={refetch}
                            refetchAll={refetch}
                          />
                        )}
                      </div>
                    }
                  />
                )}
              </FormSection>
              <FormSection>
                <SectionMessage appearance="warning">
                  <p>{viewData[viewKey].banner_content()}</p>
                </SectionMessage>
              </FormSection>

              {familyEntitlementReactivationBanner()}

              {isReactivationFlow && prevReasonCode && (
                <>
                  <FormSection>
                    <Field name="pricingPlan" label="!&#x20DD; Cancellation reason">
                      {() => (
                        <div className="cancellation-reason-block">
                          <span>
                            {prevReasonCode
                              ? CancellationReasonCodes[prevReasonCode as keyof typeof CancellationReasonCodes]
                              : 'N/A'}
                          </span>
                        </div>
                      )}
                    </Field>
                  </FormSection>
                  <br />
                  <hr className="border-line" />
                </>
              )}

              <FormSection>
                {!!formError && (
                  <InfoPanel
                    type="error"
                    message={
                      <ErrorComponents.ErrorMessageOnInfoPanel
                        title="We're unable to change the plan for the following reason"
                        message={getFormError(formError, OrderType.CANCELLATION_ORDER)}
                      />
                    }
                  />
                )}
              </FormSection>

              {subscriptionStatus && (
                <>
                  {' '}
                  <FormSection>
                    <Field name="productId" defaultValue={entitlementNumber} label="Entitlement number">
                      {() => <div>{entitlementNumber}</div>}
                    </Field>
                  </FormSection>
                  <FormSection>
                    <Field name="reasonCode" label="Reason code" defaultValue={selectedReasonCode} isRequired>
                      {() => (
                        <DropdownMenu triggerType="button" trigger={reasonCode} testId="Cancel-ent-reason-code">
                          <DropdownItemGroup>{getReasonCodeItem()}</DropdownItemGroup>
                        </DropdownMenu>
                      )}
                    </Field>
                  </FormSection>
                  <FormSection>
                    <div className="elem-with-tooltip info-icon-wrapper">
                      <Checkbox
                        isChecked={isImmediateFlag}
                        onChange={setImmediateFlag}
                        label="Effective immediately"
                        value="Cancel Immediate"
                        name="cancel-immediate"
                        testId="cancelImmediate"
                        alt="immediate"
                        isDisabled={isImmediateCancellation()}
                      />
                      <Tooltip content="Unless ‘Effective immediately’ is selected, the cancellation will take effect at the end of the current billing cycle and the subscription will be deactivated. Free plans will be canceled immediately.">
                        <InfoIcon label="more-information" size="small" />
                      </Tooltip>
                    </div>
                  </FormSection>{' '}
                </>
              )}

              {isReactivationFlow && (
                <EntitlementReactivationPane
                  entitlement={entitlement}
                  transactionAccount={transactionAccount}
                  showField={setShowField}
                  setIsFieldDisable={setIsFieldDisable}
                  creditDetails={creditDetails}
                  isFieldDisable={isFieldDisable || isApprovalRequiredForPricing || isApprovalRequiredForDiscount}
                  productSkipTrialEligible={productSkipTrialEligible}
                  updateLinkedEntitlements={setLinkedEntitlementsData}
                  linkedEntitlementsData={linkedEntitlementsData}
                  setLoading={setLoadingLinkedEntitlements}
                  loadingLinkedEntitlement={loadingLinkedEntitlement}
                  isBillEsimationEnabled={isBillEsimationEnabled}
                  formValueRef={formValueRef}
                  setEstimatedBill={setEstimatedBill}
                  estimatedBill={estimatedBill}
                  setFieldValue={setFieldValue}
                  formState={formState}
                  updateFormState={updateFormState}
                  constructRequestData={constructRequestData}
                  isReactivationDisabled={isReactivationDisabled}
                  setIsReactivationDisabled={setIsReactivationDisabled}
                  termPaymentValidation={termPaymentValidation}
                  setTermPaymentValidation={setTermPaymentValidation}
                  setGrandTotal={setGrandTotal}
                />
              )}

              {showField && (
                <FormSection>
                  <Field
                    name="supportTicketNumber"
                    label={'Support ticket number'}
                    isRequired={true}
                    validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                  >
                    {({ fieldProps, error }) => (
                      <>
                        <Textfield
                          {...fieldProps}
                          name="supportTicketNumber"
                          placeholder={'Add GSAC ticket number'}
                          testId="support-ticket-number"
                        />
                        {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                      </>
                    )}
                  </Field>
                </FormSection>
              )}

              {showField && (
                <Field
                  label={'Internal comment'}
                  name="handNote"
                  isRequired
                  validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
                >
                  {({ fieldProps, error }) => (
                    <>
                      <Textfield
                        {...fieldProps}
                        name="handNote"
                        testId="hand-note"
                        placeholder={'Add order comments'}
                      />
                      {error && <ErrorMessage testId="supportTicketLink-error">This field is required</ErrorMessage>}
                    </>
                  )}
                </Field>
              )}

              {!isBillEsimationEnabled && hasCustomPromotions ? (
                <div className="approval-info-message reactivation-page-warning-message">
                  <Icon type="warning" />
                  <span>
                    We can’t show the price estimate right now. Need help? Contact{' '}
                    <strong>#help-advocate-central</strong> channel.
                  </span>
                </div>
              ) : null}
              {!approvalProcessingForDiscount &&
              !approvalProcessingForPricing &&
              !(hasCustomPromotions && !isBillEsimationEnabled) &&
              (isApprovalRequiredForPricing || isApprovalRequiredForDiscount) ? (
                <ApprovalFormSection
                  approvalType={actualApprovalType}
                  approvalRequiredProcesses={approvalRequiredForTypes}
                  slackChannel={
                    (isApprovalRequiredForPricing ? approvalSlackChannelForPricing : approvalSlackChannelForDiscount) ??
                    ''
                  }
                  onCancel={() => closeSidebar()}
                  isLoading={
                    approvalProcessingForDiscount || cancelApprovalRequestProcessing || approvalProcessingForPricing
                  }
                  approvalItems={approvalList}
                  onEdit={() => {
                    setApprovalRequiredDiscount(false);
                    setApprovalRequiredPricing(false);
                  }}
                />
              ) : (
                showField && (
                  <FormFooter align="end">
                    <span className="btn-wrapper">
                      <Button
                        appearance="subtle"
                        onClick={() => closeSidebar()}
                        testId="cancel"
                        className="sidebar-btn-mr"
                      >
                        Close
                      </Button>
                      <ButtonGroup>
                        <LoadingButton
                          appearance={
                            subscriptionStatus
                              ? entitlement?.scheduledChanges && !isImmediateFlag
                                ? 'default'
                                : 'warning'
                              : 'primary'
                          }
                          type="submit"
                          isLoading={
                            isProcessing ||
                            estimatedBill.loading ||
                            cancelApprovalRequestProcessing ||
                            approvalProcessingForDiscount ||
                            cancelApprovalRequestProcessing ||
                            approvalProcessingForPricing
                          }
                          testId={viewData[viewKey].test_id}
                          isDisabled={
                            isActionDisabled ||
                            (hasCustomPromotions && !isBillEsimationEnabled) ||
                            (!isReactivationFlow && reasonCode === 'Select') ||
                            isReactivationDisabled
                          }
                        >
                          {viewData[viewKey].button_name}
                        </LoadingButton>
                      </ButtonGroup>
                    </span>
                  </FormFooter>
                )
              )}
              {warningModalDailogVisible && (
                <WarningDialog
                  isOpen={isConfirmDialogOpen}
                  onClose={() => setConfirmDialogOpen(false)}
                  onConfirm={() => handleConfirmDialog(getState().values)}
                  header={'Are you sure?'}
                  description={<p>All subscriptions listed on the previous screen will be canceled.</p>}
                  okLabel={'Confirm cancellation'}
                  testId={'cancelRequest-submission'}
                />
              )}
            </form>
          );
        }}
      </Form>
    </div>
  );
};

export default withApollo(memo(CancelActiveSubscription));
